import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import { Inquiry, InquiryListResponse, InquiryType } from "../../types/admin";

export type InquiryListQuery = {
  inquiry_type?: InquiryType;
  is_answered?: boolean;
  q?: string;
  page?: number;
  page_size?: number;
  /** true면 pending/answered count만 갱신하고 list는 유지 */
  countsOnly?: boolean;
};

type InquiriesState = {
  list: Inquiry[];
  totalCount: number;
  pendingCount: number;
  answeredCount: number;
  page: number;
  pageSize: number;
  loadingList: boolean;
  listError: string | null;
  detail: Inquiry | null;
  loadingDetail: boolean;
  detailError: string | null;
  savingAnswer: boolean;
};

const initialState: InquiriesState = {
  list: [],
  totalCount: 0,
  pendingCount: 0,
  answeredCount: 0,
  page: 1,
  pageSize: 20,
  loadingList: false,
  listError: null,
  detail: null,
  loadingDetail: false,
  detailError: null,
  savingAnswer: false,
};

const toQuery = (query: InquiryListQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (key === "countsOnly") {
      return;
    }
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const fetchInquiries = createAsyncThunk<
  InquiryListResponse & { countsOnly?: boolean },
  InquiryListQuery,
  { rejectValue: string }
>("inquiries/fetchInquiries", async (query, { rejectWithValue }) => {
  try {
    const data = await apiRequest<InquiryListResponse>(`/admin/inquiries${toQuery(query)}`);
    return { ...data, countsOnly: query.countsOnly };
  } catch (error) {
    const message = error instanceof Error ? error.message : "문의 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const fetchInquiryDetail = createAsyncThunk<Inquiry, number, { rejectValue: string }>(
  "inquiries/fetchInquiryDetail",
  async (inquiryId, { rejectWithValue }) => {
    try {
      return await apiRequest<Inquiry>(`/admin/inquiries/${inquiryId}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "문의 상세 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const createInquiryAnswer = createAsyncThunk<
  Inquiry,
  { inquiryId: number; answer: string },
  { rejectValue: string }
>("inquiries/createInquiryAnswer", async ({ inquiryId, answer }, { rejectWithValue }) => {
  try {
    return await apiRequest<Inquiry>(`/admin/inquiries/${inquiryId}/answer`, {
      method: "POST",
      body: { answer },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "문의 답변 등록에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const updateInquiryAnswer = createAsyncThunk<
  Inquiry,
  { inquiryId: number; answer: string },
  { rejectValue: string }
>("inquiries/updateInquiryAnswer", async ({ inquiryId, answer }, { rejectWithValue }) => {
  try {
    return await apiRequest<Inquiry>(`/admin/inquiries/${inquiryId}/answer`, {
      method: "PATCH",
      body: { answer },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "문의 답변 수정에 실패했습니다.";
    return rejectWithValue(message);
  }
});

const inquiriesSlice = createSlice({
  name: "inquiries",
  initialState,
  reducers: {
    clearInquiryDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInquiries.pending, (state, action) => {
        if (!action.meta.arg.countsOnly) {
          state.loadingList = true;
          state.listError = null;
        }
      })
      .addCase(fetchInquiries.fulfilled, (state, action) => {
        state.pendingCount = action.payload.pending_count ?? 0;
        state.answeredCount = action.payload.answered_count ?? 0;
        if (action.payload.countsOnly) {
          return;
        }
        state.loadingList = false;
        state.list = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
      })
      .addCase(fetchInquiries.rejected, (state, action) => {
        if (action.meta.arg.countsOnly) {
          return;
        }
        state.loadingList = false;
        state.listError = action.payload ?? "문의 목록 조회에 실패했습니다.";
      })
      .addCase(fetchInquiryDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchInquiryDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.detail = action.payload;
      })
      .addCase(fetchInquiryDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError = action.payload ?? "문의 상세 조회에 실패했습니다.";
      })
      .addCase(createInquiryAnswer.pending, (state) => {
        state.savingAnswer = true;
      })
      .addCase(createInquiryAnswer.fulfilled, (state, action) => {
        state.savingAnswer = false;
        state.detail = { ...state.detail, ...action.payload } as Inquiry;
        state.list = state.list.map((inquiry) =>
          inquiry.inquiry_id === action.payload.inquiry_id ? { ...inquiry, ...action.payload } : inquiry
        );
      })
      .addCase(createInquiryAnswer.rejected, (state) => {
        state.savingAnswer = false;
      })
      .addCase(updateInquiryAnswer.pending, (state) => {
        state.savingAnswer = true;
      })
      .addCase(updateInquiryAnswer.fulfilled, (state, action) => {
        state.savingAnswer = false;
        state.detail = { ...state.detail, ...action.payload } as Inquiry;
        state.list = state.list.map((inquiry) =>
          inquiry.inquiry_id === action.payload.inquiry_id ? { ...inquiry, ...action.payload } : inquiry
        );
      })
      .addCase(updateInquiryAnswer.rejected, (state) => {
        state.savingAnswer = false;
      });
  },
});

export const { clearInquiryDetail } = inquiriesSlice.actions;
export default inquiriesSlice.reducer;